/**
 * Dates, money and relative times in the locale being read.
 *
 * The tag lives in module state, set by I18nProvider on every render, so
 * `formatDate` can be called from anywhere — theme.ts re-exports it, and its
 * callers do not thread a locale through. The same shape lib/telemetry.ts uses
 * for its level.
 */

let intlTag = 'en-GB';

export function setIntlLocale(tag: string): void {
  intlTag = tag;
}

export function intlLocale(): string {
  return intlTag;
}

/** A YYYY-MM-DD date or a unix timestamp as the locale writes it: "14 Jul 2026". */
export function formatDate(iso: string | number): string {
  const d = typeof iso === 'number' ? new Date(iso * 1000) : new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return new Intl.DateTimeFormat(intlTag, { day: '2-digit', month: 'short', year: 'numeric' }).format(d);
}

/**
 * A message's clock time: "09:41", as the reader's locale writes one.
 *
 * **Milliseconds**, like every other timestamp in this app — `Message.at`,
 * `Conversation.lastAt` and `Meeting.scheduledAt` are all the wire's `Date.now()`
 * values. `formatDate` above is the copied helper and reads a bare number as
 * unix *seconds*, so passing one of ours to it gives a date in the year 58000.
 * `formatDay` below is the one to reach for.
 */
export function formatTime(ms: number): string {
  return new Intl.DateTimeFormat(intlTag, { hour: '2-digit', minute: '2-digit' }).format(new Date(ms));
}

/** A date from a millisecond timestamp: "14 Jul 2026". */
export function formatDay(ms: number): string {
  return new Intl.DateTimeFormat(intlTag, {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(new Date(ms));
}

/**
 * A call's length: "12:16" — mm:ss under an hour, h:mm:ss over it.
 *
 * Not `Intl.DurationFormat`. This sits on a transcript row beside a clock time,
 * and "12 min 16 sec" is wider than the row and reads as prose where a
 * stopwatch is meant.
 */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hh ? `${hh}:${pad(mm)}:${pad(ss)}` : `${mm}:${pad(ss)}`;
}

/**
 * "2 hours ago" for the Devices tab, in the locale's own words. Under two
 * minutes is `activeNow`, which the caller supplies translated.
 */
export function relativeTime(unixSeconds: number, activeNow = 'active now'): string {
  const diff = Math.floor(Date.now() / 1000) - unixSeconds;
  if (diff < 120) return activeNow;
  const rtf = new Intl.RelativeTimeFormat(intlTag, { numeric: 'auto' });
  if (diff < 3600) return rtf.format(-Math.floor(diff / 60), 'minute');
  if (diff < 86400) return rtf.format(-Math.floor(diff / 3600), 'hour');
  return rtf.format(-Math.floor(diff / 86400), 'day');
}

/** An invoice amount in its own currency, written the way the reader expects. */
export function formatCurrency(amount: number, currency: string, maximumFractionDigits = 2): string {
  try {
    return new Intl.NumberFormat(intlTag, { style: 'currency', currency, maximumFractionDigits }).format(
      amount
    );
  } catch {
    return `${currency} ${amount}`;
  }
}

/** A price from the pricing table, in the reader's locale rather than the
 *  region's default one, so a visitor reading English in the euro region sees
 *  "€29" and not "29 €".
 *
 *  Takes the one field it reads rather than `Region` from @shared/pricing. A
 *  `Region` still satisfies it, and dropping the import is what lets this file
 *  be copied into a repository that has locales and no prices. */
export function formatMoneyIn(amount: number, region: { currency: string }): string {
  return formatCurrency(amount, region.currency, 0);
}
